import { Logger } from 'besonders-logger'
import { CID } from 'multiformats'
import { createSignal } from 'solid-js'
import { getPersistenceStory, StoragePermsErr } from './components/mini-components'

import { initializeCryptoKeypairs } from './data/agent/AgentCrypto'

import { useAgent } from './data/agent/AgentState'
import { getApplogDB } from './data/ApplogDB'
import { ensureHomeBlockForAgent } from './data/block-utils'

import { ensureBlockStoreOpen, getBlockStore } from './ipfs/blockStore'
import { initIpfsSW } from './ipfs/ipfs-sw-init'
import { initKuboClient } from './ipfs/kubo'
import { startRosterSync } from './ipfs/roster-sync'
import { initStorage } from './ipfs/storage'
import { registerWatcherSubscriptions, startSyncService } from './ipfs/sync-service'
import { initPlugins } from './plugins/init'
import { useAppSettings } from './ui/app-settings'
import { useCurrentThread, withThread } from './ui/reactive'
import './ui/webawesome'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG, { prefix: '[init]' }) // eslint-disable-line unused-imports/no-unused-vars

// GLOBALS SIGNALS — initialized is set by App.tsx when applogDB resource resolves
export const [initialized, setInitialized] = createSignal(false)
export const [storageError, setStorageError] = createSignal(null)
export type GlobalErrorInfo = {
	source: string
	message: string
	name?: string
	stack?: string
	cause?: string
	filename?: string
	lineno?: number
	colno?: number
	extra?: string
	timestamp: string
}
export const [globalError, setGlobalError] = createSignal<GlobalErrorInfo | null>(null)

const KNOWN_ERROR_KEYS = new Set(['name', 'message', 'stack', 'cause'])
function extractErrorInfo(err: unknown): Pick<GlobalErrorInfo, 'name' | 'message' | 'stack' | 'cause' | 'extra'> {
	if (err == null || typeof err !== 'object') {
		return { message: String(err) }
	}
	const e = err as any
	let cause: string | undefined
	if (e.cause != null) {
		try {
			cause = e.cause instanceof Error
				? `${e.cause.name}: ${e.cause.message}${e.cause.stack ? '\n' + e.cause.stack : ''}`
				: JSON.stringify(e.cause)
		} catch {
			cause = String(e.cause)
		}
	}
	const extras: Record<string, unknown> = {}
	for (const key of Object.getOwnPropertyNames(e)) {
		if (!KNOWN_ERROR_KEYS.has(key)) {
			try { extras[key] = e[key] } catch { /* ignore */ }
		}
	}
	let extra: string | undefined
	if (Object.keys(extras).length > 0) {
		try { extra = JSON.stringify(extras, null, 2) } catch { extra = String(extras) }
	}
	return {
		name: typeof e.name === 'string' ? e.name : undefined,
		message: typeof e.message === 'string' ? e.message : String(err),
		stack: typeof e.stack === 'string' ? e.stack : undefined,
		cause,
		extra,
	}
}

export function formatGlobalError(e: GlobalErrorInfo): string {
	return [
		`[${e.source}] ${e.timestamp}`,
		e.name && e.name !== 'Error' ? `Name: ${e.name}` : null,
		`Message: ${e.message}`,
		e.filename ? `Location: ${e.filename}:${e.lineno ?? '?'}:${e.colno ?? '?'}` : null,
		`UserAgent: ${navigator.userAgent}`,
		`URL: ${location.href}`,
		e.cause ? `\nCause:\n${e.cause}` : null,
		e.extra ? `\nExtra:\n${e.extra}` : null,
		e.stack ? `\nStack:\n${e.stack}` : null,
	].filter(Boolean).join('\n')
}

if (typeof window !== 'undefined') {
	window.addEventListener('error', (event) => {
		ERROR('[window.error]', event.error ?? event.message, event)
		const info = extractErrorInfo(event.error)
		setGlobalError({
			source: 'window.error',
			timestamp: new Date().toISOString(),
			...info,
			message: info.message ?? event.message ?? 'Unknown error',
			filename: event.filename || undefined,
			lineno: event.lineno || undefined,
			colno: event.colno || undefined,
		})
	})
	window.addEventListener('unhandledrejection', (event) => {
		ERROR('[unhandledrejection]', event.reason, event)
		setGlobalError({
			source: 'unhandledrejection',
			timestamp: new Date().toISOString(),
			...extractErrorInfo(event.reason),
		})
	})
}

export const appInit = async function ({ /* setInitialized, */ pubFromUrl }) {
	const thread = getApplogDB()
	const currentDS = withThread(thread, () => useCurrentThread())
	DEBUG(`appInit start`, { pubFromUrl, currentDS, thread: thread.nameAndSizeUntracked })

	// Initialize IPFS SW early (non-blocking)
	initIpfsSW().catch(err => WARN('IPFS SW init failed:', err))

	const agent = useAgent()
	DEBUG(`[appInit]`, { agent })
	await initializeCryptoKeypairs(agent)
	DEBUG(`appInit storage`)
	const appSettings = withThread(thread, () => useAppSettings() as any)
	withThread(thread as any, () => ensureHomeBlockForAgent(appSettings, thread as any, agent))
	initKuboClient()
	await withThread(thread, () => initStorage())

	// yield initIPNS()

	await agent.loadIDB()

	await initPlugins()

	// const blocks = untracked(()=>query(currentDSc))
	// const roots = withThread(rawDS, () => useRoots())

	// if (!roots.length) {

	// window.addEventListener('hashchange', onHashChange)
	// 	https://web.dev/articles/persistent-storage#check_if_your_sites_storage_has_been_marked_as_persistent
	// In Tauri (Android/desktop), data lives in the app's private data dir and is
	// inherently persistent; navigator.storage.persist() always returns false in
	// WebView regardless. Skip the check there.
	if (!(window as any).isTauri && navigator.storage && navigator.storage.persist) {
		const { persistGranted, wasPersisted } = await getPersistenceStory()

		if (!persistGranted) {
			WARN(`Persisted storage status:`, { persistGranted, wasPersisted })
			setStorageError(StoragePermsErr)
		}
	}
	LOG(`appInit Starting services`)
	startSyncService(true)
	registerWatcherSubscriptions(agent.subscriptions)
	// Roster sync: follow registries / other depots' self-rosters + auto-republish own. Guarded internally.
	void startRosterSync()

	await ensureBlockStoreOpen()
	// @ts-expect-error window.
	window.blockStore = getBlockStore()
	// @ts-expect-error window.
	window.CID = CID

	// Clean up legacy IDB databases (block cache is rebuildable from applogs)
	try {
		indexedDB.deleteDatabase('ipfs/blocks')
		indexedDB.deleteDatabase('ipfs-blocks')
	} catch (_) { /* ignore */ }

	// void startHelia()
	LOG(`appInit end`)
	return () => { /* dispose */ } // window.removeEventListener('hashchange', onHashChange)
}

// const TestPage = lazy(() => import('./TestPage'))
